/**
 * [M4-09] query keys + fetchers ของ webmail — แยกไฟล์กัน conflict ตามแบบ queries-w4/queries-reports
 * keyset cursor แบบเดียวกับ ticket list (initialPageParam undefined → next_cursor)
 * [A3 · D-042] ทุก key ของข้อมูลระดับกล่องมี accountId เป็นส่วนหนึ่งของ key — สลับกล่อง = ข้อมูลใหม่ ไม่ปนข้ามกล่อง
 */
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { mailApi } from "./api-mail";

export const mailKeys = {
  account: ["mail", "account"] as const,
  labels: (accountId?: string) => ["mail", "labels", accountId ?? null] as const,
  threads: (label?: string, q?: string, accountId?: string) => ["mail", "threads", label ?? null, q ?? null, accountId ?? null] as const,
  thread: (id: string) => ["mail", "thread", id] as const,
  // [M4-10] write-side
  settings: (accountId?: string) => ["mail", "settings", accountId ?? null] as const,
  filters: (accountId?: string) => ["mail", "filters", accountId ?? null] as const,
  quarantine: (accountId?: string) => ["mail", "quarantine", accountId ?? null] as const,
  message: (id: string) => ["mail", "message", id] as const,
  draft: (id: string, accountId?: string) => ["mail", "draft", id, accountId ?? null] as const,
};

/** กล่องของตัวเอง + ธง mail.webmail (M4-02F) — 404 = ยังไม่มีกล่อง · flag ไม่มา = ปิด (fail-closed) */
export function mailAccountQuery() {
  return queryOptions({
    queryKey: mailKeys.account,
    queryFn: () => mailApi.account(),
    staleTime: 60_000,
    retry: false,
  });
}

export function mailLabelsQuery(accountId?: string) {
  return queryOptions({
    queryKey: mailKeys.labels(accountId),
    queryFn: async () => (await mailApi.labels(accountId)).items,
    staleTime: 30_000,
    retry: false,
  });
}

export function mailThreadsQuery(label?: string, q?: string, accountId?: string) {
  return infiniteQueryOptions({
    queryKey: mailKeys.threads(label, q, accountId),
    queryFn: ({ pageParam }) => mailApi.threads({ label, q, cursor: pageParam, accountId }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 15_000,
    retry: false,
  });
}

export function mailThreadQuery(id: string, accountId?: string) {
  return queryOptions({
    queryKey: mailKeys.thread(id),
    queryFn: () => mailApi.thread(id, accountId),
    staleTime: 5_000,
    retry: false,
  });
}

/** [M4-09] URL search params ของ /app/mail — label (โฟลเดอร์/ป้าย) · q (ค้นหา) · t (เธรดที่เปิดอ่าน) · account_id ([A3] กล่องที่ใช้) */
export type MailSearch = { label?: string; q?: string; t?: string; account_id?: string };

export function validateMailSearch(s: Record<string, unknown>): MailSearch {
  const pick = (k: keyof MailSearch): string | undefined => (typeof s[k] === "string" && s[k] !== "" ? (s[k] as string) : undefined);
  return { label: pick("label"), q: pick("q"), t: pick("t"), account_id: pick("account_id") };
}

// ---------------------------------------------------------------------------
// [M4-10] write-side queries — settings/filters/quarantine/message/draft
// (queryOptions อยู่รวมที่นี่ตาม pattern M4-09 · mutation fns อยู่ที่ api-mail.ts)

import type { MailDraft, MailFilter, MailMessage, MailSettings, QuarantineItem } from "@uwu/contracts";
import {
  getMailDraft,
  getMailMessage,
  getMailSettings,
  listFilters,
  listQuarantine,
} from "./api-mail";

export function mailSettingsQuery(accountId?: string) {
  return queryOptions({
    queryKey: mailKeys.settings(accountId),
    queryFn: () => getMailSettings(accountId),
    staleTime: 30_000,
    retry: false,
  });
}

export function mailFiltersQuery(accountId?: string) {
  return queryOptions({
    queryKey: mailKeys.filters(accountId),
    queryFn: () => listFilters(accountId),
    staleTime: 30_000,
    retry: false,
  });
}

export function mailQuarantineQuery(accountId?: string) {
  return queryOptions({
    queryKey: mailKeys.quarantine(accountId),
    queryFn: () => listQuarantine(accountId),
    retry: false,
  });
}

export function mailMessageQuery(id: string, accountId?: string) {
  return queryOptions({
    queryKey: mailKeys.message(id),
    queryFn: () => getMailMessage(id, accountId),
    staleTime: 10_000,
    retry: false,
  });
}

export function mailDraftQuery(id: string, accountId?: string) {
  return queryOptions({
    queryKey: mailKeys.draft(id, accountId),
    queryFn: () => getMailDraft(id, accountId),
    staleTime: 10_000,
    retry: false,
  });
}
